import { ReactNode } from 'react';
import { influencer } from '@round/api';
import { VirtualizedTable, tableHelpers } from '@round/creatorbase-ui';
import { useCreatorExplorePlatformSelection } from 'Modules/Plans/CreatorSearch/hooks/useCreatorExplorePlatformSelection';
// Reuse the real Creator Explore table cells so the pool's creators render identically to the table
// they're picked from. Each cell reads everything (images, platform, metrics) off the shared meta.
import { CreatorCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/CreatorCell/CreatorCell';
import { LocationCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/LocationCell/LocationCell';
import { TagsCell } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/TagsCell/TagsCell';
import {
    FollowersCell,
    CostCell,
    EnagementRateCell,
} from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/cells/PlatformStatCells/PlatformStatCells';
import { Column, PAGE_SIZE } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { SelectAllCreatorsHeader, SelectCreatorCell } from './components/SelectCreatorCell/SelectCreatorCell';
import { AccountsCell } from './components/AccountsCell/AccountsCell';
import { ActionsCell } from './components/ActionsCell/ActionsCell';
import { Meta } from './helpers';
import styles from './CreatorsTable.module.css';

type Creator = influencer.CreatorSearchResult;
type Row = tableHelpers.WithLoadingRow<Creator>;

type Props = Pick<
    Meta,
    | 'getCreatorImages'
    | 'getAreCreatorImagesLoading'
    | 'selectedCreatorIds'
    | 'onCreatorToggle'
    | 'onSelectAll'
    | 'onClearSelection'
    | 'onRemoveCreators'
> & {
    poolId: number | null | undefined;
    rows: Creator[];
    isLoading: boolean;
    hasError?: boolean;
    noDataLabel?: ReactNode;
};

const ROW_HEIGHT_PX = 64;
// A checkbox plus the cell's horizontal padding; the column doesn't flex.
const SELECT_COLUMN_SIZE = 56;
// A kebab button plus the cell's horizontal padding; the column doesn't flex.
const ACTIONS_COLUMN_SIZE = 56;

// Every column flexes to fill the table width; `size` is its minimum width (the table pins each cell's
// min-width to `size`, and `grow` lets it expand into the remaining space). Column order mirrors the
// Creator Explore table.
const FLEX = { className: 'grow' };

const COLUMNS: Column[] = [
    { id: 'select', header: SelectAllCreatorsHeader, cell: SelectCreatorCell, size: SELECT_COLUMN_SIZE },
    { id: 'creator', header: 'Creator', cell: CreatorCell, size: 240, meta: FLEX },
    { id: 'location', header: 'Location', cell: LocationCell, size: 150, meta: FLEX },
    { id: 'tags', header: 'Tags', cell: TagsCell, size: 200, meta: FLEX },
    { id: 'platform', header: 'Accounts', cell: AccountsCell, size: 145, meta: FLEX },
    { id: 'followers', header: 'Followers', cell: FollowersCell, size: 115, meta: FLEX },
    { id: 'cost', header: 'Cost', cell: CostCell, size: 115, meta: FLEX },
    { id: 'engagement', header: 'Engagement', cell: EnagementRateCell, size: 115, meta: FLEX },
    { id: 'actions', header: '', cell: ActionsCell, size: ACTIONS_COLUMN_SIZE },
];

export const CreatorsTable = ({
    poolId,
    rows,
    isLoading,
    hasError,
    getCreatorImages,
    getAreCreatorImagesLoading,
    noDataLabel,
    selectedCreatorIds,
    onCreatorToggle,
    onSelectAll,
    onClearSelection,
    onRemoveCreators,
}: Props) => {
    // Powers the platform badge/selector cell; a self-contained useState hook, no data fetching.
    const { getRowSelectedPlatform, onRowPlatformSelect } = useCreatorExplorePlatformSelection();

    const meta: Meta = {
        poolId,
        isLoading,
        hasError,
        getCreatorImages,
        getAreCreatorImagesLoading,
        getRowSelectedPlatform,
        onRowPlatformSelect,
        selectedCreatorIds,
        onCreatorToggle,
        onSelectAll,
        onClearSelection,
        onRemoveCreators,
    };

    const data = tableHelpers.getLoadingRows(rows, { pageSize: PAGE_SIZE, isLoading });

    return (
        <div className={styles.wrapper}>
            <VirtualizedTable<Row>
                columns={COLUMNS}
                data={data}
                meta={meta}
                virtualizerOptions={{
                    estimateSize: () => ROW_HEIGHT_PX,
                    getItemKey: (index) => rows[index]?._id ?? index,
                }}
                containerClassName={styles.tableContainer}
                noDataLabel={noDataLabel}
            />
        </div>
    );
};
